import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { CameraMeasurementPanel } from '../../components/cameraMeasurement/CameraMeasurementPanel';
import { ScanCameraMeasurementBasis } from '../../components/cameraMeasurement/ScanCameraMeasurementBasis';
import { CaseScanRegister } from '../../components/safetyCases/CaseScanRegister';
import { CaseHistoryPanel } from '../../components/safetyCases/CaseHistoryPanel';
import { ScanControlRelationDisclosure } from '../../components/safetyCases/ScanControlRelationDisclosure';
import { FirstSafetyScanFindingCard } from '../../components/safetyReports/FirstSafetyScanFindingCard';
import { SafetyCaseHomeStatusStrip } from '../../components/safetyReports/SafetyCaseHomeStatusStrip';
import { CAMERA_MEASUREMENT_GOLDENS } from '../../domain/cameraMeasurement/cameraMeasurementGoldens';
import { presentScanControlRelation } from '../../domain/safetyCases/scanControlTemporalRelation';
import { presentFirstScanFinding } from '../../domain/safetyCases/firstScanFindingPresenter';
import { buildSafetyScanProductPresentation } from '../../domain/acceptance/safetyScanProductPresentation';
import { buildSafetyScanWorkflowViewModel } from '../../domain/acceptance/safetyScanWorkflowViewModel';
import SafetyScanWorkflowPage from '../acceptanceProof/safetyScan/SafetyScanWorkflowPage';
import { CanonicalRecordHero } from '../signedControlRecord/CanonicalRecordHero';
import { SIGNED_RECORD_PREVIEW_FIXTURE } from './signedRecordPreviewFixture';

const SCAN_WINDOW = {
  start: '2026-08-10T16:00:00.000Z',
  end: '2026-08-11T16:00:00.000Z',
};

const colors = {
  ink: '#0f172a',
  ink2: '#4a5768',
  line: '#e3e8ef',
  bg: '#f4f6f9',
};

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      data-testid={`ux2-golden-${id}`}
      sx={{ bgcolor: '#fff', border: `1px solid ${colors.line}`, borderRadius: 1, mb: 2, p: 1.5 }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.08, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function PolicyNowCell({ copy }: { copy: string }) {
  return (
    <Box data-testid="overview-v2-now-cell" sx={{ p: 1.25, border: `1px solid ${colors.line}`, borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">Now · Policy status · Current measured window</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{copy}</Typography>
    </Box>
  );
}

function scanModel() {
  const presentation = buildSafetyScanProductPresentation({
    conditionLabel: 'Near miss at Warehouse Room B walkway',
    locationLine: 'Walkway zone · Camera North',
    siteLabel: 'YYZ Distribution Centre',
    cameraLabel: 'Camera North',
    caseLabel: 'Walkway near-miss case',
    funnelSource: { candidate_episode_count: 12, reviewed_count: 5, pending_review_count: 7 },
    pendingVerifyCount: 7,
    verifyRequired: true,
    readiness: { readyToSign: false, pendingVerifyCount: 7, observationDispositionPending: true, controlRecorded: true },
  });
  return buildSafetyScanWorkflowViewModel({
    presentation,
    signed: false,
    canSign: false,
    historyHref: '/acceptance/cases/case-1',
  });
}

const HISTORY_EVENTS = [
  {
    event_id: 'run:keep',
    event_type: 'scheduled_run',
    occurred_at: '2026-08-18T09:00:00Z',
    title: 'Scheduled Run',
    kind: 'MANAGEMENT STATE',
    summary: 'Completed. Safety Scan not created. This measurement window was not eligible for a Safety Scan.',
    related: { scan_created: false, no_scan_reason: 'This measurement window was not eligible for a Safety Scan.' },
  },
  {
    event_id: 'scan:1',
    event_type: 'safety_scan',
    occurred_at: '2026-08-20T09:05:00Z',
    title: 'Safety Scan',
    kind: 'MEASUREMENT',
    summary: 'Unsigned Safety Scan.',
    related: { report_id: 'scan-1', window_start_utc: SCAN_WINDOW.start, window_end_utc: SCAN_WINDOW.end },
  },
  {
    event_id: 'signed_record:1',
    event_type: 'signed_record',
    occurred_at: '2026-08-21T11:20:00Z',
    title: 'Signed Record',
    kind: 'IMMUTABLE RESULT',
    summary: 'Safety Scan scan-1. Signed by Maya Chen.',
    related: { signer_name: 'Maya Chen' },
  },
  {
    event_id: 'control:1',
    event_type: 'control',
    occurred_at: '2026-08-21T18:00:00Z',
    title: 'Control',
    kind: 'MANAGEMENT STATE',
    summary: 'Pedestrian barrier recorded.',
    related: {},
  },
  {
    event_id: 'evaluation:1',
    event_type: 'control_evaluation',
    occurred_at: '2026-08-26T09:15:00Z',
    title: 'Control Evaluation',
    kind: 'IMMUTABLE RESULT',
    summary: 'Observed rate was lower in the follow-up measurement.',
    related: {},
  },
];

const Ux2GoldensFixturePage: React.FC = () => {
  const model = scanModel();
  const crossing = presentScanControlRelation({
    windowStart: '2026-08-17T10:00:00Z',
    windowEnd: '2026-08-17T14:00:00Z',
    controlRevisionId: 'ccr-1',
    implementedAt: '2026-08-17T12:00:00Z',
  });
  return (
    <Box data-testid="ux2-goldens-fixture" sx={{ bgcolor: colors.bg, minHeight: '100vh', p: 2, color: colors.ink }}>
      <Typography component="h1" sx={{ fontSize: 18, fontWeight: 800, m: 0 }}>Edgentik · UX2 goldens</Typography>
      <Typography sx={{ fontSize: 11, color: colors.ink2, mb: 2 }}>
        Overview · Investigate · Safety Cases. Camera Measurement is physical context, not a safety event.
      </Typography>

      <Section id="A" title="A. Overview — physical activity + Policy NOW unavailable">

        <Stack spacing={1}>
          <CameraMeasurementPanel
            presentation="compact"
            siteId="yyz"
            deviceId="demo5"
            cameraId="cam_walkway_zone_demo"
            override={CAMERA_MEASUREMENT_GOLDENS.measured}
          />
          <PolicyNowCell copy="Not currently measuring" />
        </Stack>
      </Section>

      <Section id="B" title="B. Overview — measured zero">
        <Stack spacing={1}>
          <CameraMeasurementPanel
            presentation="compact"
            siteId="yyz"
            deviceId="demo5"
            cameraId="cam_walkway_zone_demo"
            override={CAMERA_MEASUREMENT_GOLDENS.measuredZero}
          />
          <PolicyNowCell copy="Needs attention" />
        </Stack>
      </Section>

      <Section id="C" title="C. Overview — limited">
        <Stack spacing={1}>
          <CameraMeasurementPanel
            presentation="compact"
            siteId="yyz"
            deviceId="demo5"
            cameraId="cam_walkway_zone_demo"
            override={CAMERA_MEASUREMENT_GOLDENS.limitedProducerGap}
          />
          <PolicyNowCell copy="Monitoring" />
        </Stack>
      </Section>

      <Section id="D" title="D. Case — zero Scans">
        <SafetyCaseHomeStatusStrip
          facts={{ hasActiveDraft: false, currentScanEstablished: false, scanCount: 0, controlMissing: true }}
          onPrimaryCta={() => undefined}
        />
        <Box sx={{ mt: 1 }}>
          <FirstSafetyScanFindingCard view={presentFirstScanFinding({ hasEligibleScan: false, logicalScanCount: 0 })} />
        </Box>
      </Section>

      <Section id="E" title="E. Case — one Scan">
        <SafetyCaseHomeStatusStrip
          facts={{
            hasActiveDraft: true,
            scanWindowLabel: 'Aug 10, 2026, 12:00 PM → Aug 11, 2026, 12:00 PM',
            scanCount: 1,
            currentScanHref: '/acceptance/scans/scan-a/verify',
          }}
          onPrimaryCta={() => undefined}
        />
        <Box sx={{ mt: 1 }}>
          <FirstSafetyScanFindingCard
            view={presentFirstScanFinding({
              hasEligibleScan: true,
              logicalScanCount: 1,
              reportId: 'scan-a',
              windowStart: SCAN_WINDOW.start,
              windowEnd: SCAN_WINDOW.end,
              locationLabel: 'Walkway zone',
              progress: { confirmed_count: 4 },
              report: { report_payload: { measurement: { usable_hours: 24 } } },
            })}
          />
        </Box>
      </Section>

      <Section id="F" title="F. Case — multiple Scans">
        <SafetyCaseHomeStatusStrip
          facts={{
            hasActiveDraft: true,
            scanWindowLabel: 'Aug 17, 2026, 12:00 PM → Aug 18, 2026, 12:00 PM',
            scanCount: 3,
            hasSignedScan: true,
            latestSignedWindow: 'Aug 10–11',
            currentScanHref: '/acceptance/scans/scan-b/verify',
          }}
          onPrimaryCta={() => undefined}
        />
        <Box sx={{ mt: 1 }}>
          <FirstSafetyScanFindingCard
            view={presentFirstScanFinding({
              hasEligibleScan: true,
              logicalScanCount: 3,
              reportId: 'scan-first',
              windowStart: '2026-07-06T20:16:00Z',
              windowEnd: '2026-07-07T20:16:00Z',
              locationLabel: 'Walkway zone',
            })}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <CaseScanRegister
            currentScanId="scan-b"
            latestSignedId="scan-a"
            rows={[
              {
                reportId: 'scan-a',
                status: 'finalized',
                unsigned: false,
                windowStart: '2026-08-16T16:00:00Z',
                windowEnd: '2026-08-17T16:00:00Z',
                isCurrentScan: false,
                isLatestSigned: true,
                revision: 1,
                relation: 'PRE',
              },
              {
                reportId: 'scan-b',
                status: 'draft',
                unsigned: true,
                windowStart: '2026-08-17T16:00:00Z',
                windowEnd: '2026-08-18T16:00:00Z',
                isCurrentScan: true,
                isLatestSigned: false,
                revision: null,
                relation: 'POST',
              },
            ]}
            onOpenScan={() => undefined}
          />
        </Box>
      </Section>

      <Section id="G" title="G. Safety Scan — measured CM basis, Layer B collapsed">
        <SafetyScanWorkflowPage
          model={model}
          measurementSource={{
            siteId: 'yyz',
            deviceId: 'demo5',
            cameraId: 'cam_walkway_zone_demo',
            windowStart: SCAN_WINDOW.start,
            windowEnd: SCAN_WINDOW.end,
          }}
          measurementOverride={CAMERA_MEASUREMENT_GOLDENS.measured}
        />
      </Section>

      <Section id="G-expanded" title="G2. Safety Scan — Layer B expanded">
        <ScanCameraMeasurementBasis
          source={{
            siteId: 'yyz',
            deviceId: 'demo5',
            cameraId: 'cam_walkway_zone_demo',
            windowStart: SCAN_WINDOW.start,
            windowEnd: SCAN_WINDOW.end,
          }}
          override={CAMERA_MEASUREMENT_GOLDENS.measured}
          defaultExpanded
        />
        <Typography variant="caption" color="text.secondary">Camera measurement during this Scan is secondary context.</Typography>
      </Section>

      <Section id="H" title="H. Safety Scan — CM measured zero">
        <ScanCameraMeasurementBasis
          source={{
            siteId: 'yyz',
            deviceId: 'demo5',
            cameraId: 'cam_walkway_zone_demo',
            windowStart: SCAN_WINDOW.start,
            windowEnd: SCAN_WINDOW.end,
          }}
          override={CAMERA_MEASUREMENT_GOLDENS.measuredZero}
          defaultExpanded
        />
      </Section>

      <Section id="I" title="I. Safety Scan — CM limited">
        <ScanCameraMeasurementBasis
          source={{
            siteId: 'yyz',
            deviceId: 'demo5',
            cameraId: 'cam_walkway_zone_demo',
            windowStart: SCAN_WINDOW.start,
            windowEnd: SCAN_WINDOW.end,
          }}
          override={CAMERA_MEASUREMENT_GOLDENS.limitedProducerGap}
          defaultExpanded
        />
      </Section>

      <Section id="J" title="J. Simultaneous presence nonzero, Near Miss confirmed count zero">
        <Typography variant="body2" sx={{ mb: 1 }}>Layer A: 0 confirmed reviewed Near Miss observations.</Typography>
        <ScanCameraMeasurementBasis
          source={{
            siteId: 'yyz',
            deviceId: 'demo5',
            cameraId: 'cam_walkway_zone_demo',
            windowStart: SCAN_WINDOW.start,
            windowEnd: SCAN_WINDOW.end,
          }}
          override={CAMERA_MEASUREMENT_GOLDENS.measured}
          defaultExpanded
        />
      </Section>

      <Section id="K" title="K. CROSSING Scan">
        <ScanControlRelationDisclosure relation={crossing} />
        <Box sx={{ mt: 1 }}>
          <ScanCameraMeasurementBasis
            source={{
              siteId: 'yyz',
              deviceId: 'demo5',
              cameraId: 'cam_walkway_zone_demo',
              windowStart: '2026-08-17T10:00:00Z',
              windowEnd: '2026-08-17T14:00:00Z',
            }}
            override={CAMERA_MEASUREMENT_GOLDENS.measured}
            defaultExpanded
          />
        </Box>
      </Section>

      <Section id="L" title="L. Signed Record — no live CM">
        <CanonicalRecordHero record={SIGNED_RECORD_PREVIEW_FIXTURE} title="Forklift-Pedestrian Proximity" />
        <Alert severity="info" sx={{ mt: 1 }} data-testid="signed-record-cm-reference">
          Camera measurement context is available on the Safety Scan and is not part of this signed result.
        </Alert>
      </Section>

      <Section id="M" title="M. History — chronology">
        <CaseHistoryPanel events={HISTORY_EVENTS} formatWhen={(value) => value || ''} density="summary" />
      </Section>
    </Box>
  );
};

export default Ux2GoldensFixturePage;
